import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import Loader from 'components/Kizen/Loader';
import FormService from 'services/FormService';
import { setSearchParams, getSearchParam } from 'hooks/useSearchParam';
import { isMobile, useWindowSize } from 'app/spacing';
import ConfirmDeletionModal from 'components/Modals/presets/ConfirmDeletion';
import { getOrderingParam, getSortValue } from 'utility/SortingHelpers';
import { setOpenMenuAbove } from 'components/Tables/helpers';
import useModal from 'components/Modals/useModal';
import {
  buildPage,
  getForms,
  updatePageConfig,
  updateFormFieldSuccess,
  duplicateForm,
  deleteForm,
  cleanDeleteFormToastMessage,
  selectAddFormModels,
  selectForms,
  selectFormsCount,
  selectFormsPageConfig,
  selectFormsDeleteToastMessage,
  addNewForm,
  updateFormAndEdit,
  updateNewlyGeneratedFormId,
  formsIsFetching,
} from 'store/formsPage/forms.redux';
import FormsTabularPage from './TabularPage';
import FormsListPage from './ListPage';
import AddModalComponent from './AddModal';
import { useTranslation } from 'react-i18next';
import { useSetTitleOnLoad } from 'hooks/useSetTitleOnLoad';
import { useToast, toastVariant } from 'components/ToastProvider';
import { useDebounce } from 'react-use';
import { maybeTrackBuilderPreview, maybeTrackSearch } from 'utility/analytics';
import { DEFAULT_DELAY } from 'utility/config';
import { deferExecution } from 'utility/defer';
import { useSyncDispatch } from 'ts-components/hooks/useSyncDispatch';
import { getCreateEntityAccess } from 'store/authentication/selectors';
import { useOpenAiChatTrigger } from 'ts-components/AiChatModal/useOpenAiChatModalTrigger';
import { useAIFeatures, AI_FEATURE_CONFIG_KEYS } from 'hooks/useAIFeatures';
import { GUARD_RAIL_CODES, FLOW_STATUS } from 'ts-components/Conversation';
import { monitoringExceptionHelper } from 'instrumentation/helpers';

export const previewForm = (...args) => {
  const preview =
    (businessUrl) =>
    (slug, windowName = '_blank') => {
      maybeTrackBuilderPreview('form', slug);
      const win = window.open(`${businessUrl}/form/${slug}`, windowName);
      win.focus();
    };

  const [url, slug, windowName] = args;
  return slug ? preview(url)(slug, windowName) : preview(url);
};

export default function FormsPage(props) {
  const { t } = useTranslation();
  useSetTitleOnLoad(props.title(t));
  const history = useHistory();
  const dispatch = useDispatch();
  const syncDispatch = useSyncDispatch();
  const { width } = useWindowSize();
  const [showToast] = useToast();

  // TODO: when hosting standalone forms we'll want to use the configurable subdomain from Settings -> Business Information
  const chosenBusinessUrl = `https://${import.meta.env.VITE_EMBED_FRONTEND_DOMAIN}`;
  // const chosenBusinessUrl = useSelector(
  //   ({ authentication }) =>
  //     !!authentication.chosenBusiness && authentication.chosenBusiness.url
  // );

  const models = useSelector(selectAddFormModels);
  const pageConfig = useSelector(selectFormsPageConfig);
  const formsCount = useSelector(selectFormsCount);
  const formsWithoutMeta = useSelector(selectForms);
  const isFetching = useSelector(formsIsFetching);
  const createEntityAccess = useSelector(getCreateEntityAccess);
  const newlyGeneratedFormId = useSelector(
    (state) => state.formsPage.newlyGeneratedFormId
  );
  const { aiFeaturesEnabled } = useAIFeatures(AI_FEATURE_CONFIG_KEYS.form_builder);

  const extractGenerationResult = useCallback((response) => {
    const runMetadata = response.run_metadata;
    const guardrailCode = runMetadata?.form_surveys?.guardrail_code;
    const guardrailMessage = runMetadata?.form_surveys?.guardrail_message;
    const sessionId = response.session_id || runMetadata?.session_id;
    const generatedForm =
      Object.keys(runMetadata?.form_surveys || {}).length > 0
        ? Object.values(runMetadata?.form_surveys)?.[0]
        : null;

    return {
      hasResult: !!generatedForm,
      sessionId,
      guardrailCode,
      guardrailMessage,
    };
  }, []);

  const onGenerationSuccess = useCallback(
    (formFlowStatusData) => {
      const { run_metadata = {}, status } = formFlowStatusData;

      // Find the ready form (has id and status: "ready")
      const allForms = Object.values(run_metadata?.form_surveys || {});
      const readyForm = allForms.find(
        (form) => form.id && form.status === 'ready'
      );

      // Handle flow with next_actions (chat session or one-shot with URL)
      const navigateUrl =
        formFlowStatusData?.next_actions?.[0]?.url || readyForm?.url;
      if (navigateUrl) {
        const guardrailCode = run_metadata?.form_surveys?.guardrail_code;
        const guardrailMessage = run_metadata?.form_surveys?.guardrail_message;

        try {
          const url = new URL(navigateUrl);
          const relativePath = url.pathname + url.search;
          if (guardrailCode && guardrailMessage) {
            showToast({
              message: guardrailMessage,
              variant:
                guardrailCode === GUARD_RAIL_CODES.REQUIRES_REVIEW
                  ? toastVariant.ALERT
                  : toastVariant.FAILURE,
              autohide: false,
            });
          } else {
            showToast({
              message: t('Form Generated Successfully'),
              variant: toastVariant.SUCCESS,
            });
          }
          setTimeout(() => {
            history.push(relativePath);
          }, 1500);
          return;
        } catch (error) {
          monitoringExceptionHelper(error, {
            extra: {
              nextActionUrl: navigateUrl,
            },
          });
        }
      }

      // Handle guardrail messages
      const guardrailCode = run_metadata?.form_surveys?.guardrail_code;
      const guardrailMessage = run_metadata?.form_surveys?.guardrail_message;

      if (guardrailCode && guardrailMessage) {
        showToast({
          message: guardrailMessage,
          variant:
            guardrailCode === GUARD_RAIL_CODES.REQUIRES_REVIEW
              ? toastVariant.ALERT
              : toastVariant.FAILURE,
          autohide: false,
        });
        if (readyForm?.id) {
          deferExecution(() => {
            dispatch(updateNewlyGeneratedFormId(readyForm.id));
          });
        }
      } else if (
        readyForm?.id &&
        status !== FLOW_STATUS.FAILED &&
        readyForm?.status !== FLOW_STATUS.FAILED
      ) {
        showToast({
          message: t('Form Generated Successfully'),
          variant: toastVariant.SUCCESS,
        });
        setTimeout(() => {
          dispatch(updateNewlyGeneratedFormId(readyForm.id));
        }, 1500);
      } else {
        showToast({
          message: t('Form Generation Failed, Please try again later'),
          variant: toastVariant.FAILURE,
        });
      }
    },
    [showToast, dispatch, history, t]
  );

  const onGenerationFailure = useCallback(
    (error) => {
      const usageError = error?.response?.data?.run_context?.usage;
      if (usageError && usageError[0]) {
        showToast({
          variant: toastVariant.FAILURE,
          message: usageError[0],
        });
      } else {
        showToast({
          variant: toastVariant.FAILURE,
          message: t('Form Generation Failed, Please try again later'),
        });
      }
    },
    [showToast, t]
  );

  // Track debounced changes to the search term and send to google analytics
  const { search } = pageConfig;
  useDebounce(() => maybeTrackSearch(search, 'forms'), DEFAULT_DELAY, [search]);

  // async effects -------------------------------------------------
  useEffect(() => {
    const sort = getSearchParam(history.location, 'sort');
    const search = getSearchParam(history.location, 'q');
    const page = getSearchParam(history.location, 'page');
    const size = getSearchParam(history.location, 'size');

    syncDispatch(
      buildPage({
        page: {
          search,
          sort,
          page: page && parseInt(page, 10),
          size: size && parseInt(size, 10),
        },
      })
    );
  }, [syncDispatch, history]);

  const forms = useMemo(() => {
    return formsWithoutMeta && formsWithoutMeta.map(setOpenMenuAbove);
  }, [formsWithoutMeta]);

  const handleChangeSearch = useCallback(
    (q) => {
      dispatch(
        updatePageConfig({
          search: q,
          page: 1,
        })
      );
      dispatch(getForms());
      deferExecution(() => {
        setSearchParams(history, { q, page: null }, { method: 'replace' });
      });
    },
    [dispatch, history]
  );

  const sortValue = useMemo(
    () => getSortValue(pageConfig.sort),
    [pageConfig.sort]
  );

  const handleSort = useCallback(
    ({ column, direction }) => {
      const value = getOrderingParam({
        column,
        direction,
      });

      dispatch(
        updatePageConfig({
          sort: value,
        })
      );
      dispatch(getForms());
      deferExecution(() => {
        setSearchParams(
          history,
          {
            sort: value,
          },
          { method: 'replace' }
        );
      });
    },
    [dispatch, history]
  );

  const handleChangePageNumber = useCallback(
    (value) => {
      dispatch(
        updatePageConfig({
          page: value,
        })
      );
      dispatch(getForms());
      deferExecution(() => {
        setSearchParams(history, {
          page: value,
        });
      });
    },
    [dispatch, history]
  );

  const handleChangePageSize = useCallback(
    (value) => {
      dispatch(
        updatePageConfig({
          size: value,
        })
      );
      dispatch(getForms());
      deferExecution(() => {
        setSearchParams(history, {
          size: value,
        });
      });
    },
    [dispatch, history]
  );

  const handleUpdateFormField = useCallback(
    async (form, patch) => {
      try {
        const updatedForm = await FormService.update(form.id, patch);
        dispatch(updateFormFieldSuccess(updatedForm));
      } catch (err) {
        let error = err;
        if (err.response && err.response.data && err.response.data.errors) {
          const errors = Object.entries(err.response.data.errors);
          if (errors && errors.length) {
            // we show only 1st error
            error = new Error(errors[0][1]);
          }
        }
        throw error;
      }
    },
    [dispatch]
  );

  const handleEditForm = useCallback(
    (id) => {
      dispatch(updateNewlyGeneratedFormId(''));
      history.push(`/forms/${id}`);
    },
    [history, dispatch]
  );

  const handleDuplicateForm = (id) => dispatch(duplicateForm(id));

  const handleConfirmDeleteForm = (id) => dispatch(deleteForm(id));

  const [deleteConfirmation, setDeleteConfirmation] = useState(null);

  const handleDeleteForm = (id) => setDeleteConfirmation({ id });

  const formDeleteToastMessage = useSelector(selectFormsDeleteToastMessage);
  useEffect(() => {
    if (formDeleteToastMessage) {
      showToast({
        variant: formDeleteToastMessage.variant,
        message: formDeleteToastMessage.message,
      });
      dispatch(cleanDeleteFormToastMessage());
    }
  }, [dispatch, formDeleteToastMessage, showToast]);

  const handleCreate = useCallback(
    (newForm) => {
      if (newForm.ai) {
        if (newForm.error) {
          showToast({
            variant: toastVariant.FAILURE,
            message: newForm.error,
          });
        } else {
          showToast({
            variant: toastVariant.SUCCESS,
            message: t('Form created successfully in Kizen AI Lab'),
          });

          dispatch(updateFormAndEdit(newForm));
        }
      } else {
        dispatch(addNewForm(newForm));
        handleEditForm(newForm.id);
      }
    },
    [dispatch, handleEditForm, showToast, t]
  );

  useEffect(() => {
    if (newlyGeneratedFormId) {
      handleEditForm(newlyGeneratedFormId);
    }
  }, [newlyGeneratedFormId, handleEditForm]);

  const [addModalProps, , addModal] = useModal({
    handleSubmit: handleCreate,
  });

  useOpenAiChatTrigger(addModal.show, addModalProps.show);

  if (isFetching) {
    return <Loader loading />;
  }
  return (
    <>
      {deleteConfirmation && (
        <ConfirmDeletionModal
          show
          onHide={() => setDeleteConfirmation(null)}
          onConfirm={async () => {
            const { id } = deleteConfirmation;
            try {
              await handleConfirmDeleteForm(id);
            } finally {
              setDeleteConfirmation(null);
            }
          }}
        >
          {t('This will permanently delete the Form.')}
        </ConfirmDeletionModal>
      )}
      {isMobile(width) ? (
        <FormsListPage
          forms={forms || []}
          formsCount={formsCount}
          search={pageConfig.search}
          onChangeSearch={handleChangeSearch}
          sort={sortValue}
          onChangeSort={handleSort}
          pageNumber={pageConfig.page || 1}
          pageSize={pageConfig.size || 50}
          onChangePageSize={handleChangePageSize}
          onChangePageNumber={handleChangePageNumber}
          onSelectPreviewForm={previewForm(chosenBusinessUrl)}
          onSelectDeleteForm={handleDeleteForm}
          createEntityAccess={createEntityAccess}
          {...props}
        />
      ) : (
        <FormsTabularPage
          forms={forms || []}
          formsCount={formsCount}
          search={pageConfig.search}
          onChangeSearch={handleChangeSearch}
          sort={sortValue}
          onChangeSort={handleSort}
          pageNumber={pageConfig.page || 1}
          pageSize={pageConfig.size || 50}
          onChangePageSize={handleChangePageSize}
          onChangePageNumber={handleChangePageNumber}
          onUpdateFormField={handleUpdateFormField}
          onSelectEditForm={handleEditForm}
          onSelectPreviewForm={previewForm(chosenBusinessUrl)}
          onSelectDuplicateForm={handleDuplicateForm}
          onSelectDeleteForm={handleDeleteForm}
          openAddModal={addModal.show}
          createEntityAccess={createEntityAccess}
          aiFeaturesEnabled={aiFeaturesEnabled}
          onGenerationSuccess={onGenerationSuccess}
          onGenerationFailure={onGenerationFailure}
          extractGenerationResult={extractGenerationResult}
          {...props}
        />
      )}
      {addModal.show && (
        <AddModalComponent
          isMobile={isMobile(width)}
          models={models}
          {...addModalProps}
        />
      )}
    </>
  );
}
